import {
  type TemplateStringifier,
  UICorePlugin,
  type SupportedVersion,
  type AttributeMap,
  type EventMap,
  template
} from '@clappr/core'
import type {Core} from 'common/components/core'
import type {MediaControl} from 'common/plugins/media-control'
import type {Options} from 'common/types/options'
import {Events} from 'common/types/events'
import * as skins from './skins'

export class Likes<T extends Options> extends UICorePlugin<T> {
  private static template = ''
  private static classNames: Record<string, string> = {}

  public static async preload(options: Options): Promise<void> {
    if (options.likes && !Likes.template) {
      const skinName = options.mediaControl?.skinName
      const loadSkin = (skinName && (skins as any)[skinName]) || skins.rambler

      if (typeof loadSkin !== 'function') {
        return
      }

      const {default: template, classNames} = await loadSkin()

      if (template) {
        Likes.template = template
      }

      if (classNames) {
        Likes.classNames = classNames
      }
    }
  }

  public core!: Core<T>

  public get name(): string {
    return 'likes'
  }

  public get supportedVersion(): SupportedVersion {
    return {min: '0.5.0'}
  }

  public get template(): TemplateStringifier {
    return template(Likes.template)
  }

  private get classNames(): Record<string, string> {
    return Likes.classNames
  }

  public get attributes(): AttributeMap {
    return {
      class: 'eplayer-likes eplayer-holdable',
      'data-likes': ''
    }
  }

  public get events(): EventMap {
    return {
      'click [data-dislike]': 'handleDislike',
      'click [data-like]': 'handleLike'
    }
  }

  private get mediaControl(): MediaControl<T> | null {
    return this.core.getPlugin('mediaControl') as MediaControl<T> | null
  }

  public toggleLike(value: boolean): void {
    const {classNames} = this

    this.trigger(value ? Events.LIKES_LIKE : Events.LIKES_DISLIKE)
    this.mediaControl?.$el
      .toggleClass(classNames.skinLiked ?? 'eplayer-skin-likes-liked', value)
      .toggleClass(
        classNames.skinDisliked ?? 'eplayer-skin-likes-disliked',
        !value
      )
  }

  public handleLike(): void {
    this.toggleLike(true)
  }

  public handleDislike(): void {
    this.toggleLike(false)
  }

  public bindEvents(): void {
    this.listenTo(
      this.core,
      Events.CORE_ACTIVE_CONTAINER_CHANGED,
      this.onActiveContainerChange
    )

    if (this.mediaControl) {
      this.listenTo(
        this.mediaControl,
        Events.MEDIACONTROL_RENDERED,
        this.render
      )
    }
  }

  public onActiveContainerChange(): void {
    this.stopListening()
    this.bindEvents()
  }

  public render(): this {
    this.$el.html(this.template())
    this.mediaControl?.$el.append(this.el)
    this.mediaControl?.$el.addClass('eplayer-skin-with-likes')

    return this
  }
}
